// Third Party
import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

// Components
import { EditBMOContactModal, EditBMOGIFPortfolio } from '../../../components/bmo-gif';
import { SelectContactModal } from '../../../components/modals';
import BMOContactInfo from './BMOContactInfo';
import BMOGIFAdditionalInfo from './BMOGIFAdditionalInfo';
import BMOGIFGraphView from './BMOGIFGraphView';
import LegacyEditBMOGIFPortfolio from '@/views/components/bmo-gif/legacy/LegacyEditBMOGIFPortfolio';
import LegacyBMOContactInfo from './legacy/LegacyBMOGIFContactInfo';
import LegacyBMOGIFAdditionalInfo from './legacy/LegacyBMOGIFAdditionalInfo';
import LegacyBMOGIFGraphView from './legacy/LegacyBMOGIFGraphView';
import LegacyBMOGIFEmptyState from './legacy/LegacyBMOGIFEmptyState';
import LegacyEditBMOContactModal from '@/views/components/bmo-gif/legacy/LegacyEditBMOContactModal';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import { resetEditContact, loadContactForEdit, getContactById } from '../../../../redux/modules/contacts';
import { setComparisonContact, saveComparison } from '../../../../redux/modules/comparison';
import {
  getGifTemplates,
  getProjectionBasisOptions,
  setDepositType,
  setDepositAmount,
  setPurchaseDate,
  setSubsequentDepositAmount,
  setSchemeType,
  setDepositStart,
  setDepositEnd,
  setNumYears,
  setFrequency,
  setProjectionBasis,
  setSalesChargeType,
  setSalesChargeAmount,
  setBaseDob,
  updateShowGraph,
  selectScenario,
  setSystematicWithdrawalMonthEnd,
  setSystematicWithdrawalMonthStart,
  setCustomRateAmount,
} from '../../../../redux/modules/bmo-gif';

// Utils
import { BMOi18n } from '../../../../utils/bmo-gif/constants';
import { CONTACT_LAYOUT } from '../../../../utils/contacts';
import GenericContact from '@/types/generic-contact';
import Comparison from '@/types/comparison';
import User from '@/types/user';
import Nullable from '@/types/nullable';
import ComparisonContact from '@/types/comparison-contact';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import BMOGIFProjectionBasis from '@/types/bmo-gif/bmo-gif-projection-basis';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';
import BMOGIFPortfolioTemplate from '@/types/bmo-gif/bmo-gif-portfolio-template';
import BMOGIFEmptyState from './BMOGIFEmptyState';
import { getThemeVersion } from '@/utils/theme';

type StateProps = {
  locale: string;
  editedContact: Partial<GenericContact>;
  comparison: Comparison;
  readOnly?: boolean;
  user?: User | null;
  portfolio: BMOGIFPortfolio;
  gifItem?: BMOGIFItem;
  projectionBasisOptions?: BMOGIFProjectionBasis[];
  selectedScenario?: BMOGIFScenario;
  showGraph: boolean;
};

type DispatchProps = {
  getContactById: (id: string) => Promise<GenericContact>;
  setComparisonContact: (contact: Nullable<GenericContact>) => void;
  resetEditContact: () => void;
  loadContactForEdit: (contact: GenericContact) => void;
  getGifTemplates: () => Promise<BMOGIFPortfolioTemplate[]>;
  getProjectionBasisOptions: () => Promise<BMOGIFProjectionBasis[]>;
  setDepositType: (value: string) => void;
  setDepositAmount: (value?: number) => void;
  setPurchaseDate: (value: Date) => void;
  setSubsequentDepositAmount: (value: number) => void;
  setSchemeType: (value: string) => void;
  setDepositStart: (value?: number) => void;
  setDepositEnd: (value?: number) => void;
  setNumYears: (value?: number) => void;
  setFrequency: (value: string) => void;
  setProjectionBasis: (value: { name: string; id: string }) => void;
  setSalesChargeType: (value: string) => void;
  setSalesChargeAmount: (value?: number) => void;
  setBaseDob: (contact: ComparisonContact) => void;
  updateShowGraph: (value: boolean) => void;
  saveComparison: () => Promise<Comparison>;
  selectScenario: (scenario: BMOGIFScenario) => void;
  setSystematicWithdrawalMonthEnd: (month?: number) => void;
  setSystematicWithdrawalMonthStart: (month?: number) => void;
  setCustomRateAmount: (value?: number) => void;
};

type State = {
  showEditBMOContactModal: boolean;
  showSelectContactModal: boolean;
};

type Props = StateProps & DispatchProps;

class BMOGIFView extends React.Component<Props, State> {
  readonly state: State = {
    showEditBMOContactModal: false,
    showSelectContactModal: false,
  };

  componentDidMount() {
    this.props.getGifTemplates();
    this.props.getProjectionBasisOptions();
  }

  showEditBMOContactModal = () => this.setState({ showEditBMOContactModal: true });

  hideEditBMOContactModal = () => this.setState({ showEditBMOContactModal: false });

  showSelectContactModal = () => this.setState({ showSelectContactModal: true });

  hideSelectContactModal = () => this.setState({ showSelectContactModal: false });

  onViewGraph = () => {
    const { gifItem } = this.props;

    if (gifItem && !gifItem.scenarios.length) {
      this.props.selectScenario(gifItem.baseScenario);
    }

    this.props.updateShowGraph(true);
  };

  onEditDetails = () => {
    this.props.updateShowGraph(false);
  };

  onSaveContact = (contact: GenericContact) => {
    this.props.setComparisonContact(contact);
    this.props.setBaseDob(contact);

    this.hideEditBMOContactModal();

    return contact;
  };

  openEditBMOContactModal = () => {
    this.props.resetEditContact();
    this.showEditBMOContactModal();
  };

  onSelectContact = (contact: GenericContact) => {
    this.props.setComparisonContact(contact);
    this.props.setBaseDob(contact);
    this.hideSelectContactModal();
  };

  onEditContact = async (id?: string) => {
    if (!id) {
      return;
    }

    const contact = await this.props.getContactById(id);

    if (!contact) {
      return;
    }

    this.props.loadContactForEdit(contact);
    this.showEditBMOContactModal();
  };

  renderDetailsView = () => {
    const {
      locale,
      comparison,
      portfolio,
      readOnly,
      projectionBasisOptions,
      user,
      setDepositType,
      setDepositAmount,
      setPurchaseDate,
      setSubsequentDepositAmount,
      setSchemeType,
      setDepositStart,
      setDepositEnd,
      setNumYears,
      setFrequency,
      setProjectionBasis,
      setSalesChargeType,
      setSalesChargeAmount,
      setSystematicWithdrawalMonthEnd,
      setSystematicWithdrawalMonthStart,
      setCustomRateAmount,
    } = this.props;
    const { isLightVersion } = getThemeVersion(user);

    if (!comparison.contact) {
      return null;
    }

    return (
      <>
        <div className="left-container d-flex flex-column">
          {isLightVersion ? (
            <>
              <BMOContactInfo locale={locale} contact={comparison.contact} onEditContact={this.onEditContact} portfolio={portfolio} readOnly={readOnly} />
              <BMOGIFAdditionalInfo
                locale={locale}
                portfolio={portfolio}
                projectionBasisOptions={projectionBasisOptions}
                readOnly={readOnly}
                comparison={comparison}
                setDepositType={setDepositType}
                setDepositAmount={setDepositAmount}
                setPurchaseDate={setPurchaseDate}
                setSubsequentDepositAmount={setSubsequentDepositAmount}
                setSchemeType={setSchemeType}
                setDepositStart={setDepositStart}
                setDepositEnd={setDepositEnd}
                setNumYears={setNumYears}
                setFrequency={setFrequency}
                setProjectionBasis={setProjectionBasis}
                setSalesChargeType={setSalesChargeType}
                setSalesChargeAmount={setSalesChargeAmount}
                setSystematicWithdrawalMonthEnd={setSystematicWithdrawalMonthEnd}
                setSystematicWithdrawalMonthStart={setSystematicWithdrawalMonthStart}
                setCustomRateAmount={setCustomRateAmount}
                contact={comparison.contact}
              />
            </>
          ) : (
            <>
              <LegacyBMOContactInfo locale={locale} contact={comparison.contact} onEditContact={this.onEditContact} portfolio={portfolio} readOnly={readOnly} />
              <LegacyBMOGIFAdditionalInfo
                locale={locale}
                portfolio={portfolio}
                projectionBasisOptions={projectionBasisOptions}
                readOnly={readOnly}
                comparison={comparison}
                setDepositType={setDepositType}
                setDepositAmount={setDepositAmount}
                setPurchaseDate={setPurchaseDate}
                setSubsequentDepositAmount={setSubsequentDepositAmount}
                setSchemeType={setSchemeType}
                setDepositStart={setDepositStart}
                setDepositEnd={setDepositEnd}
                setNumYears={setNumYears}
                setFrequency={setFrequency}
                setProjectionBasis={setProjectionBasis}
                setSalesChargeType={setSalesChargeType}
                setSalesChargeAmount={setSalesChargeAmount}
                setSystematicWithdrawalMonthEnd={setSystematicWithdrawalMonthEnd}
                setSystematicWithdrawalMonthStart={setSystematicWithdrawalMonthStart}
                setCustomRateAmount={setCustomRateAmount}
                contact={comparison.contact}
              />
            </>
          )}
        </div>

        {isLightVersion ? (
          <EditBMOGIFPortfolio readOnly={readOnly} comparison={comparison} onViewGraph={this.onViewGraph} />
        ) : (
          <LegacyEditBMOGIFPortfolio readOnly={readOnly} comparison={comparison} onViewGraph={this.onViewGraph} />
        )}
      </>
    );
  };

  renderGraphView = () => {
    const { selectedScenario, locale, portfolio, user } = this.props;
    const { isLightVersion } = getThemeVersion(user);

    return isLightVersion ? (
      <BMOGIFGraphView scenario={selectedScenario} onEditDetails={this.onEditDetails} locale={locale} portfolio={portfolio} />
    ) : (
      <LegacyBMOGIFGraphView scenario={selectedScenario} onEditDetails={this.onEditDetails} locale={locale} portfolio={portfolio} />
    );
  };

  render() {
    const { comparison, user, locale, showGraph, editedContact, ...rest } = this.props;
    const { showEditBMOContactModal, showSelectContactModal } = this.state;

    if (!user) {
      return null;
    }

    const { isLightVersion } = getThemeVersion(user);

    return (
      <>
        <div className="bmo-gif-view flex-column">
          {!comparison.contact && (
            <div className="contact-actions">
              {isLightVersion ? (
                <BMOGIFEmptyState locale={locale} openEditBMOContactModal={this.openEditBMOContactModal} showSelectContactModal={this.showSelectContactModal} />
              ) : (
                <LegacyBMOGIFEmptyState
                  locale={locale}
                  openEditBMOContactModal={this.openEditBMOContactModal}
                  showSelectContactModal={this.showSelectContactModal}
                />
              )}
            </div>
          )}

          {comparison.contact && (
            <div className={classNames('view-content', { 'd-flex flex-fill': !showGraph })}>
              {!showGraph && this.renderDetailsView()}

              {showGraph && this.renderGraphView()}
            </div>
          )}
        </div>

        <div className="version-number flex-column">{BMOi18n[locale].VERSION_NUMBER}</div>

        {isLightVersion ? (
          <EditBMOContactModal contact={editedContact} show={showEditBMOContactModal} onSave={this.onSaveContact} onCancel={this.hideEditBMOContactModal} />
        ) : (
          <LegacyEditBMOContactModal
            contact={editedContact}
            show={showEditBMOContactModal}
            onSave={this.onSaveContact}
            onCancel={this.hideEditBMOContactModal}
          />
        )}

        {showSelectContactModal && (
          <SelectContactModal
            user={user}
            show={showSelectContactModal}
            onSelect={this.onSelectContact}
            onCancel={this.hideSelectContactModal}
            layout={CONTACT_LAYOUT.CLIENT}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
  locale: state.intl.locale,
  portfolio: state.bmoGif.portfolio,
  gifItem: state.bmoGif.gifItem,
  projectionBasisOptions: state.bmoGif.projectionBasisOptions,
  selectedScenario: state.bmoGif.selectedScenario,
  showGraph: state.bmoGif.showGraph,
  editedContact: state.contacts.editContact,
});

export default connect(mapStateToProps, {
  getContactById,
  loadContactForEdit,
  setComparisonContact,
  resetEditContact,
  getGifTemplates,
  getProjectionBasisOptions,
  setDepositType,
  setDepositAmount,
  setPurchaseDate,
  setSubsequentDepositAmount,
  setSchemeType,
  setDepositStart,
  setDepositEnd,
  setNumYears,
  setFrequency,
  setProjectionBasis,
  setSalesChargeType,
  setSalesChargeAmount,
  setBaseDob,
  updateShowGraph,
  saveComparison,
  selectScenario,
  setSystematicWithdrawalMonthEnd,
  setSystematicWithdrawalMonthStart,
  setCustomRateAmount,
})(BMOGIFView);
